import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { message } from '@client/components/_ui/feedback/message';
import { notification } from '@client/components/_ui/feedback/notification';
import { tokenValues } from '@client/design-system/__generated__';
import { useFeatureFlag } from '@client/hooks/useFeatureFlag';
import { useAppStateStore } from '@client/stores/AppStateStore';
import { selectAccessToken, useAuthStore } from '@client/stores/AuthStore';
import { useDebugStore } from '@client/stores/DebugStore';
import { trpcClient, trpcOptions } from '@client/trpc/client';
import { createLogger } from '@client/utils/logger';
import { shouldEnableAppBuildVersionForRoute } from '@client/utils/version/app-build-version-gate';
import {
    clearSilentUpdateSessionMarkers,
    isWithinUpdateReloadGrace,
    markUpdateReload,
    shouldShowFallbackNotification,
} from '@client/utils/version/silent-update';
import { VERSION_MISMATCH_CONFIRM_DELAY_MS, VERSION_SNOOZE_MS } from '@client/utils/version/version-constants';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation } from '@tanstack/react-router';
import { Space, Typography } from 'antd';
import { CloudUpload, RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIsDebugInfoEnabled } from './useIsDebugInfoEnabled';
import { usePageSettled } from './usePageSettled';

const { Text } = Typography;

// Module-level logger for version checks
const log = createLogger('AppVersion');

/**
 * Check if AppVersion debug logs should be enabled
 * Logs are enabled when:
 * - isAppVersionLogsEnabled is true (checkbox in debug panel)
 * - AND (isDebugInfoEnabled or isForceDebugEnabledFlag)
 */
function isAppVersionLogsEnabledCheck(isDebugInfoEnabled: boolean): boolean {
    const { isForceDebugEnabledFlag, isAppVersionLogsEnabled } = useDebugStore.getState();
    return isAppVersionLogsEnabled && (isDebugInfoEnabled || isForceDebugEnabledFlag);
}

export function useAppBuildVersion() {
    const queryClient = useQueryClient();
    const location = useLocation();
    const isDebugInfoEnabled = useIsDebugInfoEnabled();
    const silentUpdateEnabled = useFeatureFlag('silent-version-update', false);
    const versionDismissedBuild = useAppStateStore((s) => s.versionDismissedBuild);
    const versionSnoozeUntil = useAppStateStore((s) => s.versionSnoozeUntil);
    const accessToken = useAuthStore(selectAccessToken);

    // Ref to track current debug state for use in callbacks without recreating them
    const isDebugInfoEnabledRef = useRef(isDebugInfoEnabled);
    isDebugInfoEnabledRef.current = isDebugInfoEnabled;

    // Helper to conditionally log debug info
    const debugLog = useCallback((...args: unknown[]) => {
        if (isAppVersionLogsEnabledCheck(isDebugInfoEnabledRef.current)) {
            log.debug(...args);
        }
    }, []);

    const shouldDisableQuery = useMemo(() => {
        return !shouldEnableAppBuildVersionForRoute({ accessToken, pathname: location.pathname });
    }, [accessToken, location.pathname]);

    // Mirror the latest `shouldDisableQuery` into a ref so the deferred confirmation
    // timer below reads the CURRENT route and authentication state when it fires, not
    // the values captured when it was armed (which permit the query at that point).
    const shouldDisableQueryRef = useRef(shouldDisableQuery);
    shouldDisableQueryRef.current = shouldDisableQuery;

    // Gate the initial version check on `usePageSettled` (post-`window.load`
    // grace) so the ~770 ms `getAppBuildVersion` request doesn't compete
    // with critical resources / LCP on slow connections. The 5-minute
    // polling interval, focus refetch, and visibility-change refetch below
    // all still run unchanged — they only kick in after the first fetch,
    // so mid-session version-mismatch detection is preserved.
    const pageSettled = usePageSettled();

    const {
        data: appBuildVersion,
        dataUpdatedAt,
        refetch,
        error,
    } = useQuery(
        trpcOptions.appSetting.getAppBuildVersion.queryOptions(undefined, {
            enabled: !shouldDisableQuery && pageSettled,
            staleTime: 0, // Always consider data stale for immediate refetch on focus
            refetchInterval: 1000 * 60 * 5, // 5 minutes
            refetchOnWindowFocus: true,
            throwOnError: false,
        }),
    );

    const key = 'app-build-version';
    const [isOpen, setIsOpen] = useState(false);
    const isReloadingRef = useRef(false);
    const lastRefetchTimeRef = useRef(0);

    // Two-observation mismatch confirmation. The first sighting of a mismatched server
    // build only arms a delayed confirmation refetch; the update UI (popup or silent
    // update) is allowed only once the SAME build is still reported after the
    // confirmation window. A single stale observation — a backend process whose 30 s
    // version cache predates the deploy, or a one-off flap — therefore never prompts.
    const mismatchCandidateRef = useRef<{ build: string; firstSeenAt: number; confirmTimer: number } | null>(null);

    const clearMismatchCandidate = useCallback(() => {
        if (mismatchCandidateRef.current) {
            window.clearTimeout(mismatchCandidateRef.current.confirmTimer);
            mismatchCandidateRef.current = null;
        }
    }, []);

    useEffect(() => {
        useAppStateStore.getState().actions.setSilentUpdateEnabled(silentUpdateEnabled);
    }, [silentUpdateEnabled]);

    // Log errors for debugging - helps diagnose version check failures
    useEffect(() => {
        if (error) {
            console.warn('[AppVersion] Failed to fetch app version:', error.message);
            debugLog('Version check error', {
                message: error.message,
                code: 'code' in error ? error.code : undefined,
            });
        }
    }, [error, debugLog]);

    // Refetch on tab activation (visibility change) or window focus
    // This replaces useIdleTimer which only detected user activity, not tab switches
    useEffect(() => {
        const REFETCH_DEBOUNCE_MS = 1000; // Prevent duplicate refetches when both events fire

        const triggerRefetch = (reason: 'visibilitychange' | 'focus' | 'polling') => {
            const now = Date.now();
            if (now - lastRefetchTimeRef.current < REFETCH_DEBOUNCE_MS) {
                debugLog('Version check skipped (debounced)', { reason });
                return;
            }

            if (!shouldDisableQuery && pageSettled) {
                debugLog('Version check triggered', { reason });
                lastRefetchTimeRef.current = now;
                void refetch();
            } else {
                debugLog('Version check skipped (route/auth not ready or page not settled)', { reason });
            }
        };

        const handleVisibilityChange = () => {
            if (!document.hidden) {
                triggerRefetch('visibilitychange');
            }
        };

        const handleWindowFocus = () => {
            triggerRefetch('focus');
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', handleWindowFocus);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleWindowFocus);
        };
    }, [debugLog, refetch, shouldDisableQuery, pageSettled]);

    // Handle "Later" button - dismiss notification for this specific build number
    // Popup won't reappear for this build, but WILL appear for a newer build
    const handleLater = useCallback(() => {
        notification.destroy(key);
        setIsOpen(false);

        const appState = useAppStateStore.getState();
        const currentServerBuild = appBuildVersion?.buildNumber;
        if (currentServerBuild) {
            appState.actions.setVersionDismissedBuild(currentServerBuild);
        }

        appState.actions.clearSilentUpdate();
        clearSilentUpdateSessionMarkers();

        debugLog('Version notification dismissed for build', {
            dismissedBuild: currentServerBuild,
        });
    }, [appBuildVersion?.buildNumber, debugLog]);

    // Handle the "X" close button — snooze the notification ("remind me later").
    // Unlike "Later" (which dismisses this build outright), the snooze re-shows the toast
    // once VERSION_SNOOZE_MS elapses, as long as the version is still mismatched. We do NOT
    // touch silent-update state here: a snooze hides the toast, it doesn't cancel a pending
    // silent reload.
    const handleSnooze = useCallback(() => {
        notification.destroy(key);
        setIsOpen(false);

        const snoozeUntil = Date.now() + VERSION_SNOOZE_MS;
        useAppStateStore.getState().actions.setVersionSnoozeUntil(snoozeUntil);

        debugLog('Version notification snoozed — will remind later', { snoozeUntil });
    }, [debugLog]);

    // Handle app update - reload with cache bust
    const handleAppUpdate = useCallback(async () => {
        isReloadingRef.current = true;
        notification.destroy(key);
        setIsOpen(false);

        const appState = useAppStateStore.getState();
        const currentServerBuild = appBuildVersion?.buildNumber;
        if (currentServerBuild) {
            appState.actions.setVersionDismissedBuild(currentServerBuild);
        }

        appState.actions.clearSilentUpdate();
        clearSilentUpdateSessionMarkers();

        message.loading('Updating application...', 10);

        // Invalidate the query to get the latest app build version in case it has changed
        await queryClient.invalidateQueries(trpcOptions.appSetting.getAppBuildVersion.pathFilter());
        const latestBuildVersion = await trpcClient.appSetting.getAppBuildVersion.query();
        if (latestBuildVersion) {
            const { version: versionFromServer, updatedAt } = latestBuildVersion;
            appState.actions.setAppBuildNumber(versionFromServer);
            appState.actions.setAppBuildLastUpdatedAt(updatedAt);
        }

        // Suppress mismatch handling briefly after the reload — stale backend version
        // caches must not re-show the prompt to a client that just loaded the new bundle.
        markUpdateReload();

        // Use URL API to properly replace the t parameter (avoids accumulation like ?t=123&t=456)
        const url = new URL(window.location.href);
        url.searchParams.set('t', Date.now().toString());
        window.location.href = url.toString();
    }, [appBuildVersion?.buildNumber, queryClient]);

    const showVersionNotification = useCallback(() => {
        if (isOpen || isReloadingRef.current) {
            return;
        }

        debugLog('Showing new version notification');
        setIsOpen(true);

        notification.info({
            key,
            title: (
                <Space align="center">
                    <Text strong className="text-lg">
                        New Version Available
                    </Text>
                </Space>
            ),
            description: (
                <div className="py-1">
                    <Text>
                        A new version of the application is available with the latest features and improvements.
                    </Text>
                </div>
            ),
            duration: 0,
            placement: 'bottomRight',
            className: 'app-version-notification',
            icon: <CloudUpload style={{ width: '18px', height: '18px', color: tokenValues.color.info[500] }} />,
            // Closing via the X snoozes the toast ("remind me later") — see handleSnooze. Persisting a
            // snooze deadline (rather than a bare setIsOpen(false)) is essential: the comparison effect
            // depends on `isOpen`, so without it the effect would re-run with the mismatch unresolved and
            // immediately re-show the toast — and every subsequent poll / focus / tab-switch would re-pop it.
            onClose: handleSnooze,
            actions: (
                <Space className="mt-2">
                    <Button type="tertiary" onClick={handleLater}>
                        Later
                    </Button>
                    <Button type="primary" icon={<RefreshCw />} onClick={handleAppUpdate}>
                        Reload now
                    </Button>
                </Space>
            ),
        });
    }, [debugLog, handleAppUpdate, handleLater, handleSnooze, isOpen]);

    useEffect(() => {
        if (!silentUpdateEnabled) {
            return;
        }

        const handleVisibilityChange = () => {
            if (!document.hidden && shouldShowFallbackNotification()) {
                showVersionNotification();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [showVersionNotification, silentUpdateEnabled]);

    // The comparison effect below does not re-run when wall-clock time passes, so once a
    // "remind me later" snooze is active we schedule a timer to clear it on expiry. Clearing
    // `versionSnoozeUntil` re-triggers the comparison effect, which re-shows the toast if the
    // version is still mismatched. A persisted snooze whose deadline has already passed (e.g.
    // restored on a fresh load) is cleared immediately.
    useEffect(() => {
        if (versionSnoozeUntil === null) {
            return;
        }

        const clearSnooze = () => useAppStateStore.getState().actions.setVersionSnoozeUntil(null);
        const remaining = versionSnoozeUntil - Date.now();

        if (remaining <= 0) {
            clearSnooze();
            return;
        }

        const timer = window.setTimeout(clearSnooze, remaining);
        return () => window.clearTimeout(timer);
    }, [versionSnoozeUntil]);

    useEffect(() => {
        const localBuildNumber = __APP_BUILD_NUMBER__?.toString();

        // If the route/auth state disables the query or no app version data exists, don't show the modal
        if (shouldDisableQuery || !appBuildVersion) {
            // Navigating onto an excluded route or losing auth mid-confirmation must cancel
            // any pending confirmation refetch — otherwise the still-armed timer would force
            // a version-check request the query is meant to skip.
            if (shouldDisableQuery) {
                clearMismatchCandidate();
            }
            if (isAppVersionLogsEnabledCheck(isDebugInfoEnabled)) {
                debugLog('Version check skipped', {
                    reason: shouldDisableQuery ? 'query disabled by route/auth state' : 'no app version data yet',
                    clientBuildNumber: localBuildNumber,
                });
            }
            return;
        }

        const appState = useAppStateStore.getState();

        // ALWAYS update store when we have data from server
        // This ensures version info is displayed regardless of version mismatch
        appState.actions.setAppBuildNumber(appBuildVersion.version);
        appState.actions.setAppBuildLastUpdatedAt(appBuildVersion.updatedAt);

        const { buildNumber: buildNumberFromServer } = appBuildVersion;

        // Skip version comparison in development mode (when either version is '0')
        // This prevents false positives during local development
        const isDevMode = localBuildNumber === '0' || buildNumberFromServer === '0';

        // Check if this specific build was already dismissed by the user
        // Uses reactive versionDismissedBuild from hook (not getState()) to handle Zustand persist rehydration
        const isDismissed = versionDismissedBuild === buildNumberFromServer;
        const versionsMatch = localBuildNumber === buildNumberFromServer;
        const hasMismatch =
            !isDevMode && Boolean(buildNumberFromServer) && Boolean(localBuildNumber) && !versionsMatch && !isDismissed;
        // "Remind me later" snooze (X-close). shouldShowFallbackNotification already honours this for
        // the silent-update path; isSnoozed gates the explicit-notification path below.
        const isSnoozed = versionSnoozeUntil !== null && Date.now() < versionSnoozeUntil;
        const fallbackNotificationReady = hasMismatch && silentUpdateEnabled && shouldShowFallbackNotification();

        if ((isDismissed || isSnoozed) && isOpen) {
            notification.destroy(key);
            setIsOpen(false);
            return;
        }

        if (isAppVersionLogsEnabledCheck(isDebugInfoEnabled)) {
            debugLog('Version comparison', {
                clientBuildNumber: localBuildNumber,
                serverBuildNumber: buildNumberFromServer,
                serverVersion: appBuildVersion.version,
                serverUpdatedAt: appBuildVersion.updatedAt,
                isDevMode,
                isDismissed,
                dismissedBuild: versionDismissedBuild,
                isSnoozed,
                versionSnoozeUntil,
                versionsMatch,
                silentUpdateEnabled,
                silentUpdatePending: appState.silentUpdatePending,
                silentUpdateBuildNumber: appState.silentUpdateBuildNumber,
                fallbackNotificationReady,
                isAlreadyReloading: isReloadingRef.current,
            });
        }

        if (!hasMismatch) {
            // Only a REAL match resolves the mismatch. A '0' build from either side means
            // "unknown" (dev mode, or the server temporarily couldn't read version.json) and
            // carries no information — it must not clear a pending silent update, an active
            // snooze, or the confirmation candidate.
            if (versionsMatch) {
                clearMismatchCandidate();
                appState.actions.clearSilentUpdate();
                clearSilentUpdateSessionMarkers();
                // No outstanding mismatch — drop any stale snooze so it can't suppress a future build.
                if (appState.versionSnoozeUntil !== null) {
                    appState.actions.setVersionSnoozeUntil(null);
                }
            }
            return;
        }

        // Post-update reload grace: right after "Reload now" / a silent update, a backend
        // process with a stale version cache can still report the OLD build to a client
        // already on the NEW bundle. Ignore mismatches until the grace window elapses.
        if (isWithinUpdateReloadGrace()) {
            debugLog('Version mismatch ignored — within post-update reload grace window', {
                buildNumberFromServer,
            });
            return;
        }

        // Require the same mismatched build to still be reported after the confirmation
        // window before surfacing anything. First sighting arms a refetch to re-check.
        const candidate = mismatchCandidateRef.current;
        if (!candidate || candidate.build !== buildNumberFromServer) {
            clearMismatchCandidate();
            const confirmTimer = window.setTimeout(() => {
                // Mirror the visibility/focus guard: refetch() ignores the query's `enabled`
                // flag, so skip the forced re-check if the user has since navigated to an
                // excluded route or lost auth on a protected route. Read the ref (not the
                // captured value) so this reflects the current route/auth state at fire time.
                if (!shouldDisableQueryRef.current) {
                    void refetch();
                }
            }, VERSION_MISMATCH_CONFIRM_DELAY_MS + 1_000);
            mismatchCandidateRef.current = { build: buildNumberFromServer, firstSeenAt: Date.now(), confirmTimer };
            debugLog('Version mismatch observed once — awaiting confirmation', { buildNumberFromServer });
            return;
        }

        if (Date.now() - candidate.firstSeenAt < VERSION_MISMATCH_CONFIRM_DELAY_MS) {
            debugLog('Version mismatch awaiting confirmation window', {
                buildNumberFromServer,
                firstSeenAt: candidate.firstSeenAt,
            });
            return;
        }

        if (silentUpdateEnabled) {
            if (!appState.silentUpdatePending || appState.silentUpdateBuildNumber !== buildNumberFromServer) {
                if (appState.silentUpdateBuildNumber && appState.silentUpdateBuildNumber !== buildNumberFromServer) {
                    clearSilentUpdateSessionMarkers();
                }
                appState.actions.setSilentUpdatePending(buildNumberFromServer);
                debugLog('Silent update pending — will reload on next safe navigation', {
                    buildNumberFromServer,
                });
            }

            if (fallbackNotificationReady) {
                showVersionNotification();
            }

            return;
        }

        appState.actions.clearSilentUpdate();
        clearSilentUpdateSessionMarkers();
        if (!isSnoozed) {
            showVersionNotification();
        }
    }, [
        appBuildVersion,
        // dataUpdatedAt re-runs this effect on every refetch even when React Query's
        // structural sharing keeps the data reference identical — required so the
        // confirmation refetch above is actually re-evaluated.
        dataUpdatedAt,
        clearMismatchCandidate,
        debugLog,
        isDebugInfoEnabled,
        isOpen,
        refetch,
        showVersionNotification,
        shouldDisableQuery,
        silentUpdateEnabled,
        versionDismissedBuild,
        versionSnoozeUntil,
    ]);
}
